import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';

const ProfilePage: React.FC = () => {
  const { user } = useAuth();
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // MFA Setup state
  const [showSetup, setShowSetup] = useState(false);
  const [qrCodeUri, setQrCodeUri] = useState('');
  const [secret, setSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  // Change Password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setMfaEnabled(user.mfa_enabled || false);
    }
  }, [user]);

  const startMFASetup = async () => {
    setError(null);
    setLoading(true);
    try {
      const res = await authAPI.mfaSetup();
      setSecret(res.data.secret);
      setQrCodeUri(res.data.provisioning_uri);
      setShowSetup(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to initialize MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const enableMFA = async () => {
    if (!totpCode || totpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const res = await authAPI.mfaEnable(totpCode);
      setMfaEnabled(true);
      setBackupCodes(res.data.backup_codes || []);
      setSuccess('MFA enabled successfully! Save your backup codes.');
      setTotpCode('');
      // Refresh user data
      const meRes = await authAPI.me();
      if (meRes.data) {
        // User state will be updated by context
      }
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Invalid TOTP code');
    } finally {
      setLoading(false);
    }
  };

  const disableMFA = async () => {
    if (!window.confirm('Are you sure you want to disable MFA? This will reduce your account security.')) {
      return;
    }
    const code = window.prompt('Enter your current TOTP code to confirm:');
    if (!code) return;

    setError(null);
    setLoading(true);
    try {
      await authAPI.mfaDisable(code);
      setMfaEnabled(false);
      setShowSetup(false);
      setQrCodeUri('');
      setSecret('');
      setBackupCodes([]);
      setSuccess('MFA disabled successfully');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to disable MFA');
    } finally {
      setLoading(false);
    }
  };

  const downloadBackupCodes = () => {
    const text = `Avika POSH Platform - MFA Backup Codes\n\nAccount: ${user?.email}\nGenerated: ${new Date().toLocaleString()}\n\n${backupCodes.join('\n')}\n\n⚠️ Store these codes securely. Each code can only be used once.`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `avika-backup-codes-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmNewPassword) {
      setError('New passwords do not match');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setPasswordLoading(true);
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully');
      setShowChangePassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to change password');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Profile & Security</h1>
        <p className="text-sm text-gray-600 mt-1">Manage your account settings and security preferences</p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-2.5 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-start gap-2.5 p-4 rounded-lg bg-green-50 border border-green-200 text-green-800 text-sm">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {success}
        </div>
      )}

      {/* User Info Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h2>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Full Name</span>
            <span className="text-sm font-medium text-gray-900">{user?.full_name || 'N/A'}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Email</span>
            <span className="text-sm font-medium text-gray-900">{user?.email}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Role</span>
            <span className="text-sm font-medium text-gray-900">{user?.role}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-gray-600">Department</span>
            <span className="text-sm font-medium text-gray-900">{user?.department || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* MFA Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Two-Factor Authentication (MFA)</h2>
            <p className="text-sm text-gray-600 mt-1">
              Add an extra layer of security using TOTP authenticator apps
            </p>
          </div>
          <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
            mfaEnabled 
              ? 'bg-green-100 text-green-800' 
              : 'bg-gray-100 text-gray-600'
          }`}>
            {mfaEnabled ? 'Enabled' : 'Disabled'}
          </div>
        </div>

        {!mfaEnabled && !showSetup && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-900">
                <strong>Recommended:</strong> Enable MFA to protect your account from unauthorized access. 
                You'll need an authenticator app like Google Authenticator or Microsoft Authenticator.
              </p>
            </div>
            <button
              onClick={startMFASetup}
              disabled={loading}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50"
            >
              {loading ? 'Setting up...' : 'Enable MFA'}
            </button>
          </div>
        )}

        {showSetup && !mfaEnabled && (
          <div className="space-y-5">
            <div className="border-t pt-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Step 1: Scan QR Code</h3>
              <p className="text-sm text-gray-600">
                Open your authenticator app and scan this QR code:
              </p>
              {qrCodeUri && (
                <div className="bg-white p-4 rounded-lg border-2 border-gray-200 inline-block">
                  <img 
                    src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrCodeUri)}`}
                    alt="MFA QR Code"
                    className="w-48 h-48"
                  />
                </div>
              )}
              <div className="bg-gray-50 p-3 rounded border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Or enter this secret manually:</p>
                <code className="text-sm font-mono text-gray-900">{secret}</code>
              </div>
            </div>

            <div className="border-t pt-5 space-y-4">
              <h3 className="font-semibold text-gray-900">Step 2: Verify Code</h3>
              <p className="text-sm text-gray-600">
                Enter the 6-digit code from your authenticator app:
              </p>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg text-center text-lg font-mono"
                  maxLength={6}
                />
                <button
                  onClick={enableMFA}
                  disabled={loading || totpCode.length !== 6}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? 'Verifying...' : 'Verify & Enable'}
                </button>
                <button
                  onClick={() => {
                    setShowSetup(false);
                    setQrCodeUri('');
                    setSecret('');
                    setTotpCode('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {mfaEnabled && (
          <div className="space-y-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-sm text-green-900">
                <strong>✓ MFA is enabled.</strong> Your account is protected with two-factor authentication.
              </p>
            </div>
            <button
              onClick={disableMFA}
              disabled={loading}
              className="px-4 py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 disabled:opacity-50"
            >
              {loading ? 'Disabling...' : 'Disable MFA'}
            </button>
          </div>
        )}

        {/* Backup Codes Display */}
        {backupCodes.length > 0 && (
          <div className="mt-6 border-t pt-6">

      {/* Change Password Card */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
            <p className="text-sm text-gray-600 mt-1">
              Update your password to keep your account secure
            </p>
          </div>
        </div>

        {!showChangePassword ? (
          <button
            onClick={() => setShowChangePassword(true)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Current Password
              </label>
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={8}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
              <p className="text-xs text-gray-500 mt-1">
                Must contain at least 8 characters, 1 uppercase letter, and 1 digit
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Confirm New Password
              </label>
              <input
                type="password"
                value={confirmNewPassword}
                onChange={(e) => setConfirmNewPassword(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent outline-none"
              />
            </div>

            <div className="flex gap-3">
              <button
                type="submit"
                disabled={passwordLoading}
                className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {passwordLoading ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowChangePassword(false);
                  setCurrentPassword('');
                  setNewPassword('');
                  setConfirmNewPassword('');
                  setError(null);
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <div>
                  <h3 className="font-semibold text-yellow-900">Save Your Backup Codes</h3>
                  <p className="text-sm text-yellow-800 mt-1">
                    Each code can be used once if you lose access to your authenticator app. Store them securely.
                  </p>
                </div>
              </div>
              <div className="bg-white rounded border border-yellow-300 p-3">
                <div className="grid grid-cols-2 gap-2 font-mono text-sm">
                  {backupCodes.map((code, i) => (
                    <div key={i} className="text-gray-700">{code}</div>
                  ))}
                </div>
              </div>
              <button
                onClick={downloadBackupCodes}
                className="px-4 py-2 bg-yellow-600 text-white rounded-lg font-medium hover:bg-yellow-700 text-sm"
              >
                📥 Download Backup Codes
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProfilePage;
